import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Timer } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import ExportButton from "../ExportButton";
import { downloadBlob } from "../../../lib/downloadBlob";
import { exportName, exportPath } from "../../../lib/exportFile";
import { KIND, formatMinutes, monthRange, thisMonth } from "../../../lib/tickets";

/**
 * The owner's time on tickets in a month (14.10.2026): the total, split by
 * kind, and every entry as a CSV for the owner's own records. Owner only.
 */
export default function TimeSummary({ refreshKey }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);

  const range = monthRange(month);

  const load = useCallback(async () => {
    if (!range) return;
    try {
      const r = await api.get("/admin/tickets/time/summary", { params: range });
      setData(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Timpul nu a putut fi încărcat."));
    }
  }, [range?.start, range?.end]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = (format) => range && downloadBlob(
    exportPath("/admin/tickets/time/export", { start: range.start, end: range.end }, format),
    exportName(`timp-tichete-${month}`, format));

  const parts = Object.entries(data?.by_kind || {})
    .sort((a, b) => b[1] - a[1])
    .map(([kind, minutes]) => `${(KIND[kind]?.short || kind).toLowerCase()} ${formatMinutes(minutes)}`);

  return (
    <div className="mb-4 flex items-center gap-3 flex-wrap rounded-xl border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm"
      data-testid="tickets-time-summary">
      <Timer size={16} className="text-[#965EA7]" />
      <span className="text-[#6B7280]">Timpul meu în</span>
      <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Luna"
        className="rounded-md border border-[#E5E7EB] px-2 py-1 text-xs" data-testid="tickets-time-month" />
      <strong className="text-[#373A45] tabular-nums" data-testid="tickets-time-total">{formatMinutes(data?.total_minutes || 0)}</strong>
      {parts.length > 0 && <span className="text-xs text-[#6B7280]">· {parts.join(" · ")}</span>}
      <span className="flex-1" />
      <ExportButton onExport={download} disabled={!data?.entries} label="Exportă timpul"
        className="rounded-full px-3 py-1 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50 inline-flex items-center gap-1.5"
        testid="tickets-time-export" />
    </div>
  );
}
